import { useEffect, useState } from 'react';
import type { CustomerApiClient, CustomerCart } from '../api-client.ts';
import { customerAnalytics } from '../analytics/client.ts';
import { Button, Card, EmptyState, Price, UnavailableSection } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';
import { requestWithRetry } from '../request-retry.ts';
import { CheckoutFlow, MoneySummary } from './checkout.tsx';

type CartRead = CustomerCart;
type CartGroup = CartRead['groups'][number];
type CartItem = CartGroup['items'][number];

const unavailableStates = new Set(['OUT_OF_STOCK', 'LISTING_UNAVAILABLE', 'NOT_SERVICEABLE']);

function isUnavailable(item: CartItem): boolean {
  return unavailableStates.has(item.availabilityState);
}

function formatMoney(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(minor / 100);
}

function revalidationMessage(item: CartItem, currency: string): string | null {
  if (item.priceChanged) {
    return `Price updated from ${formatMoney(item.capturedPriceCents, currency)} to ${formatMoney(item.currentUnitPriceCents, currency)}.`;
  }
  if (item.availabilityState === 'PARTIAL') {
    return `Only ${item.quantity} left — quantity reduced.`;
  }
  if (item.availabilityState === 'OUT_OF_STOCK') return 'Out of stock.';
  if (item.availabilityState === 'LISTING_UNAVAILABLE') return 'This item is no longer available.';
  if (item.availabilityState === 'NOT_SERVICEABLE')
    return 'This item is not available in your service area.';
  return null;
}

export interface CartScreenProps {
  cart: CartRead;
  client: Pick<CustomerApiClient, 'addresses' | 'analytics' | 'cart' | 'checkout' | 'orders'>;
  onCartChanged?: (cart: CartRead) => void;
}

function seller(ownershipType: string, sellerDisplayName: string): string {
  return ownershipType === 'BEAUTY_BURST_DIRECT' ? 'Beauty Burst' : sellerDisplayName;
}

function CartLine({
  item,
  client,
  sellerDisplayName,
  ownershipType,
  currency,
  onCartChanged,
  disabled = false,
}: {
  item: CartItem;
  client: Pick<CustomerApiClient, 'cart' | 'analytics'>;
  sellerDisplayName: string;
  ownershipType: string;
  currency: string;
  onCartChanged?: (cart: CartRead) => void;
  disabled?: boolean;
}) {
  const [quantity, setQuantity] = useState(String(item.quantity));
  const [working, setWorking] = useState(false);
  const updateQuantity = async (nextValue: string) => {
    setQuantity(nextValue);
    const nextQuantity = Number(nextValue);
    if (
      !Number.isInteger(nextQuantity) ||
      nextQuantity < 1 ||
      nextQuantity > 999 ||
      nextQuantity === item.quantity
    ) {
      return;
    }
    setWorking(true);
    try {
      const updatedCart = await client.cart.changeQuantity(item.id, nextQuantity);
      onCartChanged?.(updatedCart);
    } finally {
      setWorking(false);
    }
  };

  return (
    <li style={{ borderTop: `1px solid ${semanticColor.borderDefault}`, paddingTop: space[3] }}>
      <p
        style={{
          ...typeStyle('body'),
          color: semanticColor.textPrimary,
          fontWeight: 700,
          marginTop: 0,
        }}
      >
        Listing item {item.listingId}
      </p>
      <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
        Sold by {seller(ownershipType, sellerDisplayName)}
      </p>
      {item.priceChanged ? (
        <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
          <span style={{ textDecoration: 'line-through' }}>
            {formatMoney(item.capturedPriceCents, currency)}
          </span>{' '}
          <span>{formatMoney(item.currentUnitPriceCents, currency)}</span>
        </p>
      ) : (
        <Price priceCents={item.currentUnitPriceCents} currency={currency} />
      )}
      {revalidationMessage(item, currency) === null ? null : (
        <p role="status" style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
          {revalidationMessage(item, currency)}
        </p>
      )}
      {disabled ? null : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const field = event.currentTarget.elements.namedItem(`quantity-${item.id}`);
            const nextValue = field instanceof HTMLInputElement ? field.value : quantity;
            void updateQuantity(nextValue);
          }}
          style={{ display: 'flex', gap: space[3], alignItems: 'end' }}
        >
          <label style={{ display: 'grid', gap: space[1] }}>
            <span style={{ ...typeStyle('label'), color: semanticColor.textPrimary }}>
              Quantity
            </span>
            <input
              aria-label={`Quantity for listing item ${item.listingId}`}
              name={`quantity-${item.id}`}
              type="number"
              min="1"
              max="999"
              value={quantity}
              disabled={working}
              onChange={(event) => setQuantity(event.currentTarget.value)}
            />
          </label>
          <Button variant="secondary" type="submit" disabled={working}>
            Update quantity
          </Button>
          <Button
            variant="ghost"
            disabled={working}
            onClick={() => {
              void client.cart
                .removeItem(item.id)
                .then(() => requestWithRetry(() => client.cart.read()))
                .then(onCartChanged)
                .catch(() => undefined);
            }}
          >
            Remove
          </Button>
        </form>
      )}
    </li>
  );
}

/** Multi-seller cart: seller groups are distinct future deliveries, never a blended inventory pool. */
export function CartScreen({ cart, client, onCartChanged }: CartScreenProps) {
  const [currentCart, setCurrentCart] = useState(cart);

  useEffect(() => {
    setCurrentCart(cart);
  }, [cart]);

  useEffect(() => {
    void (client.analytics ?? customerAnalytics())
      .track({
        name: 'cart.viewed',
        subjectKind: 'CART',
        subjectId: currentCart.cartId,
      })
      .catch(() => undefined);
  }, [currentCart.cartId, client]);
  const activeGroups = currentCart.groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !isUnavailable(item)),
    }))
    .filter((group) => group.items.length > 0);
  const unavailableItems = currentCart.groups.flatMap((group) =>
    group.items.filter(isUnavailable).map((item) => ({
      item,
      ownershipType: group.ownershipType,
      sellerDisplayName: group.sellerDisplayName,
    })),
  );
  const mayShipSeparately = activeGroups.length > 1;

  const receiveCart = (nextCart: CartRead) => {
    setCurrentCart(nextCart);
    onCartChanged?.(nextCart);
  };

  if (currentCart.groups.length === 0) {
    return (
      <section aria-labelledby="cart-title">
        <h1 id="cart-title" style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}>
          Your cart
        </h1>
        <section aria-labelledby="cart-status-title">
          <h2
            id="cart-status-title"
            style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
          >
            Cart status
          </h2>
          <EmptyState
            title="Your cart is empty"
            description="Browse products from local stores to add something to your cart."
            action={<a href={customerHref('/categories')}>Browse beauty products</a>}
          />
        </section>
      </section>
    );
  }

  return (
    <section aria-labelledby="cart-title">
      <h1 id="cart-title" style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}>
        Your cart
      </h1>
      {mayShipSeparately ? (
        <p style={{ ...typeStyle('body'), color: semanticColor.textSecondary }}>
          Items from different sellers may arrive as separate deliveries.
        </p>
      ) : null}

      <section aria-labelledby="available-cart-items-title">
        <h2
          id="available-cart-items-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Cart items
        </h2>
        {activeGroups.map((group) => {
          const quotedGroup = currentCart.quote.groups.find(
            (candidate) => candidate.cartSellerGroupId === group.id,
          );
          return (
            <Card
              key={group.id}
              aria-label={`Sold by ${seller(group.ownershipType, group.sellerDisplayName)}`}
            >
              <h3
                style={{
                  ...typeStyle('body'),
                  color: semanticColor.textPrimary,
                  fontWeight: 700,
                  margin: 0,
                }}
              >
                Sold by {seller(group.ownershipType, group.sellerDisplayName)}
              </h3>
              {quotedGroup === undefined ? (
                <p
                  role="alert"
                  style={{ ...typeStyle('bodySm'), color: semanticColor.statusDangerText }}
                >
                  This seller’s current price is unavailable.
                </p>
              ) : (
                <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
                  {group.itemCount} {group.itemCount === 1 ? 'item' : 'items'} ·{' '}
                  {formatMoney(quotedGroup.subtotalMinor, currentCart.currency)}
                </p>
              )}
              <ul
                style={{ display: 'grid', gap: space[3], listStyle: 'none', margin: 0, padding: 0 }}
              >
                {group.items.map((item) => (
                  <CartLine
                    key={item.id}
                    item={item}
                    client={client}
                    sellerDisplayName={group.sellerDisplayName}
                    ownershipType={group.ownershipType}
                    currency={currentCart.currency}
                    onCartChanged={receiveCart}
                  />
                ))}
              </ul>
            </Card>
          );
        })}
      </section>
      <MoneySummary cart={currentCart} heading="Cart price summary" />

      {unavailableItems.length === 0 ? null : (
        <section aria-labelledby="unavailable-cart-items-title" style={{ marginTop: space[6] }}>
          <UnavailableSection
            title="Unavailable items"
            reason="These items are excluded from seller subtotals and cannot move forward to checkout."
          />
          <h2
            id="unavailable-cart-items-title"
            style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
          >
            Items needing attention
          </h2>
          <ul
            aria-label="Unavailable cart items"
            style={{ display: 'grid', gap: space[3], listStyle: 'none', padding: 0 }}
          >
            {unavailableItems.map(({ item, ownershipType, sellerDisplayName }) => (
              <li key={item.id}>
                <Card>
                  <p
                    style={{
                      ...typeStyle('body'),
                      color: semanticColor.textPrimary,
                      fontWeight: 700,
                    }}
                  >
                    Listing item {item.listingId}
                  </p>
                  <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
                    {ownershipType === 'BEAUTY_BURST_DIRECT'
                      ? 'Sold by Beauty Burst'
                      : `Sold by ${sellerDisplayName}`}
                  </p>
                  <CartLine
                    item={item}
                    client={client}
                    sellerDisplayName={sellerDisplayName}
                    ownershipType={ownershipType}
                    currency={currentCart.currency}
                    onCartChanged={receiveCart}
                    disabled
                  />
                </Card>
              </li>
            ))}
          </ul>
        </section>
      )}

      {client.addresses === undefined ? null : (
        <section aria-label="Checkout" style={{ marginTop: space[6] }}>
          <CheckoutFlow cart={currentCart} client={client} onCartChanged={receiveCart} />
        </section>
      )}
    </section>
  );
}
